import React, {useEffect, useState} from 'react';
import {ChevronDown, Eye, Languages, MessageCircle, User} from 'lucide-react';
import {CVService} from '../services/cvService';
import ProfileSection from './sections/ProfileSection';
import TimelineSection from './sections/TimelineSection';
import SkillsSection from './sections/SkillsSection';
import LanguagesSection from './sections/LanguagesSection';
import HobbiesSection from './sections/HobbiesSection';
import ChatPanel from './ChatPanel';
import Starfield from './Starfield';
import {Button} from './ui/button';
import {Card, CardContent} from './ui/card';
import {TypewriterProvider} from '../contexts/TypewriterContext';
// Images supprimées - seront récupérées depuis l'API si nécessaire
// import backgroundImage from '../cvtheques/vincent/background.jpeg';
// import profilImage from '../cvtheques/vincent/profil.jpg';

const CVInteractive = () => {
  const [activeSection, setActiveSection] = useState(null);
  const [chatVisible, setChatVisible] = useState(false);
  const [initialQuestion, setInitialQuestion] = useState(null);
  const [currentLocale, setCurrentLocale] = useState('fr');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [selectedAngle, setSelectedAngle] = useState('authentique');
  const [selectedReaderProfile, setSelectedReaderProfile] = useState(null);
  const [showAngleDropdown, setShowAngleDropdown] = useState(false);
  const [showReaderDropdown, setShowReaderDropdown] = useState(false);
  const [showImages, setShowImages] = useState(false); // Par défaut masqué
  
  // Créer une instance du service CV
  const [cvService] = useState(() => new CVService(currentLocale, 'parcours', 'rh'));

  // Angles de lecture (obligatoire - tes casquettes)
  const readingAngles = [
    { id: 'technique', label: { fr: '🔧 Technique', en: '🔧 Technical' }, description: { fr: 'Expertise, archi, code, infra', en: 'Expertise, architecture, code, infrastructure' } },
    { id: 'directeur', label: { fr: '👔 Directeur', en: '👔 Director' }, description: { fr: 'Pilotage, stratégie, vision, management', en: 'Leadership, strategy, vision, management' } },
    { id: 'authentique', label: { fr: '👤 Authentique', en: '👤 Authentic' }, description: { fr: 'Récit brut, personnel', en: 'Raw story, personal' } }
  ];

  // Profils de lecteur (optionnel - adaptation au public)
  const readerProfiles = [
    { id: 'recruteur', label: { fr: '🎯 Recruteur / RH', en: '🎯 Recruiter / HR' } },
    { id: 'manager', label: { fr: '🧑‍💼 Manager', en: '🧑‍💼 Manager' } },
    { id: 'cto', label: { fr: '🧑‍💻 CTO', en: '🧑‍💻 CTO' } }
  ];
  const [cvData, setCvData] = useState({
    profile: null,
    experiences: [],
    skills: [],
    languages: [],
    hobbies: []
  });

  // Load CV data on component mount and when parameters change
  useEffect(() => {
    const loadCVData = async () => {
      console.log('🚀 Starting CV data load...');
      setLoading(true);
      setError(null);
      
      try {
        // Mapper les angles vers les paramètres API (selon AngleEnum de l'API)
        const angleMapping = {
          'technique': 'technique',
          'directeur': 'directeur', // Correction : directeur → directeur (pas management)
          'authentique': 'authentique' // Correction : authentique → authentique (pas parcours)
        };
        
        // Mapper les profils lecteur vers audience API (selon AudienceEnum de l'API)
        const audienceMapping = {
          'recruteur': 'rh',
          'manager': 'manager',
          'cto': 'cto'
        };
        
        const apiAngle = angleMapping[selectedAngle] || 'authentique'; // Valeur par défaut de l'API
        const apiAudience = selectedReaderProfile ? audienceMapping[selectedReaderProfile] : 'general'; // Valeur par défaut de l'API
        
        console.log(`📡 Loading CV with parameters: locale=${currentLocale}, angle=${apiAngle}, audience=${apiAudience}`);
        console.log('🔧 API Base URL:', process.env.REACT_APP_API_BASE_URL);
        
        // Mettre à jour les paramètres et récupérer les données depuis l'API
        console.log('📞 Calling API...');
        await cvService.updateParameters(currentLocale, apiAngle, apiAudience, true); // Force refresh au premier chargement
        
        // Récupérer les données mises à jour depuis le service
        const updatedCvData = {
          profile: cvService.getProfile(),
          experiences: cvService.getExperiences(),
          skills: cvService.getSkills(),
          languages: cvService.getLanguages(),
          hobbies: cvService.getHobbies(),
          backgroundImage: cvService.getBackgroundImage() // Ajouter l'image de background base64
        };
        
        console.log('✅ CV data loaded:', updatedCvData);
        console.log('🖼️ Background image available:', !!updatedCvData.backgroundImage);
        setCvData(updatedCvData);
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Error loading CV data:', error);
        setError(error.message);
        setLoading(false);
      }
    };

    console.log('🔄 useEffect triggered with dependencies:', { currentLocale, selectedAngle, selectedReaderProfile });
    loadCVData();
  }, [currentLocale, selectedAngle, selectedReaderProfile, cvService]);

  // Handle question click from sections
  const handleQuestionClick = (sectionId, question = null) => {
    setActiveSection(sectionId);
    setInitialQuestion(question);
    setChatVisible(true);
  };

  // Fonction pour mettre à jour une expérience spécifique après refresh API
  const updateExperience = (experienceId, updatedExperienceData) => {
    console.log('🔄 Updating experience in parent state:', experienceId);
    console.log('📦 Raw API response:', updatedExperienceData);
    
    // L'API retourne un objet complet avec toutes les métadonnées + data array
    if (!updatedExperienceData || typeof updatedExperienceData !== 'object') {
      console.error('❌ Invalid data format received from API:', updatedExperienceData);
      return;
    }
    
    // Vérifier que l'objet contient les données attendues
    if (!updatedExperienceData.data || !Array.isArray(updatedExperienceData.data)) {
      console.error('❌ API response missing data array:', updatedExperienceData);
      return;
    }
    
    console.log('✅ Valid API response with', updatedExperienceData.data.length, 'achievements');
    
    setCvData(prevData => {
      const updatedExperiences = prevData.experiences.map(exp => {
        if (exp.id === experienceId) {
          console.log('🔄 Updating experience:', exp.id);
          console.log('📋 Current experience structure:', exp);
          
          // Utiliser toutes les données de l'API (métadonnées + achievements)
          const updatedExp = {
            ...updatedExperienceData, // Utiliser toutes les nouvelles données de l'API
            id: experienceId // S'assurer que l'ID reste cohérent
          };
          
          console.log('✅ Updated experience structure:', updatedExp);
          return updatedExp;
        }
        return exp;
      });
      
      return {
        ...prevData,
        experiences: updatedExperiences
      };
    });
  };

  // Handle language toggle
  const toggleLanguage = () => {
    const newLocale = currentLocale === 'fr' ? 'en' : 'fr';
    setCurrentLocale(newLocale);
  };

  // Handle angle change (obligatoire)
  const handleAngleChange = (angleId) => {
    setSelectedAngle(angleId);
    setShowAngleDropdown(false);
    console.log(`Angle sélectionné: ${angleId}`);
  };

  // Handle reader profile change (optionnel)
  const handleReaderProfileChange = (profileId) => {
    setSelectedReaderProfile(profileId);
    setShowReaderDropdown(false);
    console.log(`Profil lecteur sélectionné: ${profileId}`);
  };

  // Get current angle info
  const getCurrentAngle = () => {
    return readingAngles.find(a => a.id === selectedAngle);
  };

  // Get current reader profile info
  const getCurrentReaderProfile = () => {
    return readerProfiles.find(p => p.id === selectedReaderProfile);
  };

  // Reset chat when changing sections
  useEffect(() => {
    if (activeSection) {
      setInitialQuestion(null);
    }
  }, [activeSection]);

  // Couleur des « neurones » du fond étoilé selon l'angle de lecture
  // (cyan = technique, ambre = directeur, violet = authentique)
  useEffect(() => {
    const neurons = {
      technique: { r: 143, g: 227, b: 240, energy: 0.55 },
      directeur: { r: 245, g: 165, b: 36, energy: 0.7 },
      authentique: { r: 162, g: 138, b: 230, energy: 0.5 },
    };
    const detail = neurons[selectedAngle] || neurons.technique;
    window.dispatchEvent(new CustomEvent('cv-neurons', { detail }));
  }, [selectedAngle]);

  // Handle scroll effect for dynamic header
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsScrolled(scrollTop > 200); // Trigger after 200px scroll
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showAngleDropdown && !event.target.closest('.angle-dropdown')) {
        setShowAngleDropdown(false);
      }
      if (showReaderDropdown && !event.target.closest('.reader-dropdown')) {
        setShowReaderDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showAngleDropdown, showReaderDropdown]);




  return (
    <TypewriterProvider>
      <div className="min-h-screen text-foreground relative">
        {/* Fond cosmique : nébuleuse + étoiles + réseau de nœuds au scroll + Gargantua */}
        <Starfield />
      {/* Dynamic Header - White background with size changes */}
      <header className={`sticky top-0 z-20 bg-space-900/80 backdrop-blur-md border-b border-hair shadow-lg transition-all duration-500 ease-in-out ${
        isScrolled ? 'py-2' : 'py-6'
      }`}>
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Profile Info - Changes size when scrolled */}
            <div className={`flex items-center transition-all duration-500 ease-in-out ${
              isScrolled ? 'space-x-3' : 'space-x-6'
            }`}>
              {/* Profile Image */}
              <div className={`rounded-full bg-space-800 shadow-lg ring-2 ring-hair overflow-hidden transition-all duration-500 ease-in-out ${
                isScrolled ? 'w-12 h-12' : 'w-20 h-20'
              }`}>
                {cvService.getProfileImage() ? (
                  <img 
                    src={cvService.getProfileImage()} 
                    alt="Profile" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-space-600 to-space-700 flex items-center justify-center">
                    <User size={isScrolled ? 24 : 36} className="text-white" />
                  </div>
                )}
              </div>
              
              {/* Name and Title */}
              <div className="text-ink">
                <h1 className={`font-bold tracking-tight transition-all duration-500 ease-in-out ${
                  isScrolled ? 'text-xl' : 'text-4xl mb-2'
                }`}>
                  {cvData.profile?.fullName || 'Vincent Ostermann'}
                </h1>
                {!isScrolled && (
                  <h2 className="text-xl font-medium text-ink-muted mb-1">
                    {cvService.getTranslatedValue(cvData.profile?.title) || 'Directeur technique, architecte logiciel & ingénieur système'}
                  </h2>
                )}
                {isScrolled && (
                  <span className="text-sm text-ink-muted">
                    {cvService.getTranslatedValue(cvData.profile?.title) || 'Directeur technique, architecte logiciel & ingénieur système'}
                  </span>
                )}
              </div>
            </div>
            
            {/* Controls - Double Dimension Selectors + Language */}
            <div className="flex items-center space-x-3">
              {/* Étape 1: Angle de lecture (obligatoire) */}
              <div className="relative angle-dropdown group">
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-space-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  {currentLocale === 'fr' ? 'Mise en avant du rôle' : 'Reading angle'}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-space-700"></div>
                </div>
                
                <Button
                  variant="outline"
                  size={isScrolled ? "sm" : "default"}
                  onClick={() => setShowAngleDropdown(!showAngleDropdown)}
                  className={`transition-all duration-500 ease-in-out bg-space-800/70 hover:bg-space-700 border-hair text-ink-muted hover:text-ink ${
                    isScrolled ? 'h-8 px-2 text-xs' : 'h-10 px-3 text-sm'
                  }`}
                >
                  <Eye size={isScrolled ? 14 : 16} className="mr-2" />
                  <span className={isScrolled ? 'text-xs' : 'text-sm'}>
                    {getCurrentAngle()?.label[currentLocale]}
                  </span>
                  <ChevronDown size={isScrolled ? 12 : 14} className="ml-2" />
                </Button>

                {/* Dropdown Menu Angles */}
                {showAngleDropdown && (
                  <div className="absolute top-full right-0 mt-1 bg-space-800 border border-hair rounded-lg shadow-xl z-50 min-w-64 py-1">
                    <div className="px-3 py-2 border-b border-hair">
                      <p className="text-xs font-medium text-ink-muted">
                        {currentLocale === 'fr' ? 'Choisir un angle de lecture' : 'Choose a reading angle'}
                      </p>
                    </div>
                    {readingAngles.map((angle) => (
                      <button
                        key={angle.id}
                        onClick={() => handleAngleChange(angle.id)}
                        className={`w-full text-left px-3 py-3 hover:bg-space-800 transition-colors ${
                          selectedAngle === angle.id ? 'bg-space-700 text-ink font-medium' : 'text-ink-muted'
                        }`}
                      >
                        <div className="text-sm font-medium">{angle.label[currentLocale]}</div>
                        <div className="text-xs text-ink-dim mt-1">{angle.description[currentLocale]}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Étape 2: Profil lecteur (optionnel) */}
              <div className="relative reader-dropdown group">
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-space-700 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                  {currentLocale === 'fr' ? 'Adapter au lecteur (optionnel)' : 'Adapt to reader (optional)'}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-space-700"></div>
                </div>
                
                <Button
                  variant="outline"
                  size={isScrolled ? "sm" : "default"}
                  onClick={() => setShowReaderDropdown(!showReaderDropdown)}
                  className={`transition-all duration-500 ease-in-out bg-space-800/70 hover:bg-space-700 border-hair text-ink-muted hover:text-ink ${
                    isScrolled ? 'h-8 px-2 text-xs' : 'h-10 px-3 text-sm'
                  } ${selectedReaderProfile ? 'border-hair-bright' : 'border-dashed'}`}
                >
                  <User size={isScrolled ? 14 : 16} className="mr-2" />
                  <span className={isScrolled ? 'text-xs' : 'text-sm'}>
                    {getCurrentReaderProfile()?.label[currentLocale] || (currentLocale === 'fr' ? 'Général' : 'General')}
                  </span>
                  <ChevronDown size={isScrolled ? 12 : 14} className="ml-2" />
                </Button>

                {/* Dropdown Menu Reader Profiles */}
                {showReaderDropdown && (
                  <div className="absolute top-full right-0 mt-1 bg-space-800 border border-hair rounded-lg shadow-xl z-50 min-w-48 py-1">
                    <div className="px-3 py-2 border-b border-hair">
                      <p className="text-xs font-medium text-ink-muted">
                        {currentLocale === 'fr' ? 'Adapter au profil du lecteur' : 'Adapt to reader profile'}
                      </p>
                    </div>
                    <button
                      onClick={() => handleReaderProfileChange(null)}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-space-800 transition-colors ${
                        !selectedReaderProfile ? 'bg-space-700 text-ink font-medium' : 'text-ink-muted'
                      }`}
                    >
                      {currentLocale === 'fr' ? '📄 Général' : '📄 General'}
                    </button>
                    {readerProfiles.map((profile) => (
                      <button
                        key={profile.id}
                        onClick={() => handleReaderProfileChange(profile.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-space-800 transition-colors ${
                          selectedReaderProfile === profile.id ? 'bg-space-700 text-ink font-medium' : 'text-ink-muted'
                        }`}
                      >
                        {profile.label[currentLocale]}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Bouton « Images » masqué : aucune illustration (images_url) en base
                  depuis le refactor d'élimination de Mistral. À réactiver quand des
                  images seront fournies par réalisation. */}

              {/* Language Toggle Button */}
              <Button
                variant="outline"
                size={isScrolled ? "sm" : "default"}
                onClick={toggleLanguage}
                className={`transition-all duration-500 ease-in-out bg-space-800/70 hover:bg-space-700 border-hair text-ink-muted hover:text-ink ${
                  isScrolled ? 'h-8 px-3 text-xs' : 'h-10 px-4 text-sm'
                }`}
                title={currentLocale === 'fr' ? 'Switch to English' : 'Passer en français'}
              >
                <Languages size={isScrolled ? 14 : 16} className="mr-2" />
                {currentLocale === 'fr' ? 'EN' : 'FR'}
              </Button>
            </div>
            
          </div>
        </div>
      </header>
      
      {/* Loading and Error States */}
      {loading && (
        <div className="container mx-auto py-8 relative z-10">
          <Card className="bg-space-800/60 backdrop-blur-md border-hair">
            <CardContent className="p-8 text-center">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan"></div>
                <span className="text-ink-muted font-medium">
                  {currentLocale === 'fr' ? 'Chargement du CV...' : 'Loading CV...'}
                </span>
              </div>
              <p className="text-ink-dim text-sm">
                {currentLocale === 'fr' 
                  ? 'Récupération des données depuis l\'API...' 
                  : 'Fetching data from API...'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {error && (
        <div className="container mx-auto py-8 relative z-10">
          <Card className="bg-red-500/10 backdrop-blur-md border-red-500/30">
            <CardContent className="p-8 text-center">
              <div className="text-red-400 mb-4">
                <span className="font-medium">
                  {currentLocale === 'fr' ? 'Erreur de chargement' : 'Loading Error'}
                </span>
              </div>
              <p className="text-red-300 text-sm mb-4">
                {error}
              </p>
              <p className="text-red-400 text-xs">
                {currentLocale === 'fr' 
                  ? 'Utilisation des données locales de secours.' 
                  : 'Using local fallback data.'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Main content */}
      {!loading && (
        <div className="container mx-auto flex flex-col md:flex-row relative z-10">
          {/* CV Section */}
          <div 
            className="flex-1 overflow-y-auto relative"
            style={{ minHeight: '80vh' }}
          >
            <div className="space-y-8">
              {/* Profile Section */}
              <ProfileSection 
                profile={cvData.profile} 
                cvService={cvService} 
              />
              
              {/* Timeline Section */}
              <TimelineSection 
                experiences={cvData.experiences} 
                cvService={cvService}
                onQuestionClick={handleQuestionClick}
                showImages={showImages}
                onUpdateExperience={updateExperience}
              />
              
              {/* Skills Section */}
              <SkillsSection 
                skills={cvData.skills} 
                cvService={cvService}
              />
              
              {/* Languages Section */}
              <LanguagesSection 
                languages={cvData.languages} 
                cvService={cvService}
              />
              
              {/* Hobbies Section */}
              <HobbiesSection 
                hobbies={cvData.hobbies} 
                cvService={cvService}
              />
            </div>
          </div>
        </div>
      )}
        
      {/* Chat Panel - Positioned fixed bottom-right */}
      {chatVisible && (
        <div className="fixed bottom-6 right-6 z-40 w-96 max-w-[90vw]">
          <ChatPanel
            isVisible={chatVisible}
            onClose={() => setChatVisible(false)}
            activeSection={activeSection}
            cvService={cvService}
            initialQuestion={initialQuestion}
          />
        </div>
      )}
      
      {/* Floating chat button */}
      {!chatVisible && (
        <div className="fixed bottom-6 right-6 z-50">
          <button
            onClick={() => setChatVisible(true)}
            className="rounded-full shadow-xl h-14 w-14 bg-amber hover:bg-amber-soft border-0 flex items-center justify-center transition-colors duration-200"
            style={{
              animation: 'bounce 2s infinite',
              boxShadow: '0 0 24px 4px color-mix(in oklab, var(--amber) 45%, transparent)'
            }}
            title={currentLocale === 'fr' ? 'Ouvrir le chat' : 'Open chat'}
          >
            <MessageCircle size={28} color="#05070d" strokeWidth={2} />
          </button>
        </div>
      )}
      </div>
    </TypewriterProvider>
  );
};

export default CVInteractive;